import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import MessageContainer from "./MessageContainer";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import { fromNow } from "../../../helpers/common.helper";
import AppContext from "../../../AppContext";
import { format } from "date-fns";
import { ReactComponent as ReplySvg } from "../../../assets/chat/reply.svg";
import { ReactComponent as DeleteSvg } from "../../../assets/chat/delete.svg";
import { ReactComponent as OptionMenuSvg } from "../../../assets/chat/option-menu.svg";
import pinImage from "../../../assets/chat/pin.png";
import useChat from "../../../hooks/useChat";

const assetsUri = import.meta.env.ASSETS_URI;

const GOOGLE_MAPS_API_KEY = import.meta.env.GOOGLE_MAPS_API_KEY;

const UserMessage = ({
  isFirstMessage,
  message,
  lastMessage,
  userId,
  onDelete,
  onReply,
  onScrollToReply,
  onPin,
  onUnpin,
}) => {
  const { user, displayImages, confirm } = useContext(AppContext);
  const {
    currentSession: session,
    getCurrentSession,
    isHiddenMessage,
  } = useContext(ChatSessionContext);
  const { deleteChat } = useChat();
  const [isToggleOption, setIsToggleOption] = useState(false);
  const [isPinned, setIsPinned] = useState(
    getCurrentSession().pins?.find((p) => p === message._id) !== undefined
  );

  const _handleMessageUnpinnedEvent = ({ detail }) => {
    setIsPinned(false);
  };

  useEffect(() => {
    document.addEventListener(
      `message:unpinned-${message._id}`,
      _handleMessageUnpinnedEvent
    );

    return () => {
      document.removeEventListener(
        `message:unpinned-${message._id}`,
        _handleMessageUnpinnedEvent
      );
    };
  }, [message._id]);

  useEffect(() => {
    // const _currentSession = get;
    // console.log("session", getCurrentSession().pins);
    // setIsPinned(!!session.pins?.find((p) => p === message._id));
  }, [session]);

  // * Determine if message is from someone else
  const isOwner = userId === message.createdBy;

  console.log(userId);
  console.log(message);

  const _displayImages = () => {
    if (!message.images?.length) return null;

    const getImageUri = (path) => {
      if (path.startsWith("blob:")) {
        return path;
      }

      return `${assetsUri}/${path}`;
    };

    return (
      <div className="chat-content-msg-images">
        {message.images.map((image, index) => {
          const _hasMoreImages = message.images.length > 3;
          const _isLastImageInset = index === 2;

          return (
            <div
              className="chat-content-msg-images__img"
              onClick={() => {
                displayImages(
                  message.images.map((_) => _.file),
                  index
                );
              }}
            >
              <img src={getImageUri(image.file)} />
              {/* {_isLastImageInset && _hasMoreImages && (
                <div className="chat-content-msg-images__more">
                  +{message.images.length - 2}
                </div>
              )} */}
            </div>
          );
        })}
      </div>
    );
  };

  const _displayFiles = () => {
    if (!message.attachments?.length) return null;

    const downloadFile = (attachment) => {
      const uri = attachment.file.startsWith("blob:")
        ? attachment.file
        : `${assetsUri}/${attachment.file}`;

      fetch(uri)
        // check to make sure you didn't have an unexpected failure (may need to check other things here depending on use case / backend)
        .then((resp) =>
          resp.status === 200
            ? resp.blob()
            : Promise.reject("something went wrong")
        )
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = attachment.name;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => alert("oh no!"));
    };

    function _getFileSize(size) {
      let _size = Math.trunc(+(size ?? 0) / 1000);

      if (_size < 1000) {
        return `${_size} KB`;
      }

      _size = _size / 1000;

      if (_size < 1000) {
        return `${_size.toFixed(2)} MB`;
      }

      return;
    }

    return (
      <div className="chat-content-msg-attachments">
        {message.attachments.map((attachment, index) => {
          return (
            <div
              className="chat-content-msg-attachments-item"
              onClick={() => {
                downloadFile(attachment);
              }}
            >
              {/* <div className="chat-content-msg-attachments-item__icon"></div> */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  flexGrow: 1,
                  justifyContent: "center",
                }}
              >
                <span className="chat-content-msg-attachments-item__name">
                  {attachment.name}
                </span>
                <span className="chat-content-msg-attachments-item__size">
                  Size: {_getFileSize(attachment.size)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const _displayLocation = () => {
    if (!message.location) return null;

    const { lat, lng, name, address } = message.location;

    const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=220x120&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;

    return (
      <div
        className="chat-content-msg-location"
        onClick={() => {
          // * Open google maps
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
          );
        }}
      >
        <img alt="static-map" src={staticMapUrl} width={220} />
        <div className="chat-content-msg-location-info">
          {name && (
            <span className="chat-content-msg-location-info__name">{name}</span>
          )}
          <span
            className={`chat-content-msg-location-info__address ${
              name ? "chat-content-msg-location-info__address--minimal" : ""
            }`}
          >
            {address}
          </span>
        </div>
      </div>
    );
  };

  const handleDeleteClick = () => {
    confirm(
      "Unsend message?",
      "Others may have already seen it. This can't be undone",
      [
        {
          id: "cancel",
          label: "Cancel",
          variant: "secondary",
        },
        {
          id: "unsend",
          label: "Unsend",
          variant: "primary",
        },
      ],
      async (action) => {
        if (action.id === "cancel") return;

        await deleteChat(message._id);
        onDelete && onDelete();
      }
    );
  };

  const handleMouseLeftMessageContainer = () => {
    setIsToggleOption(false);
  };

  const _getEndUsersName = () => {
    return session.endUser?.name ?? session.endUser?.firstname;
  };

  const _getReplyContent = () => {
    if (message.replied.deleted || isHiddenMessage(message.replied.createdOn)) {
      return (
        <div className="chat-content-reply__msg chat-content-reply__msg--italic">
          Message unavailable
          {isPinned && <img className="chat-content-pin" src={pinImage} />}
        </div>
      );
    }

    if (message.replied.msg) {
      return (
        <div className="chat-content-reply__msg" onClick={onScrollToReply}>
          {message.replied.msg}
          {isPinned && <img className="chat-content-pin" src={pinImage} />}
        </div>
      );
    }

    if (message.replied.attachments?.length > 0) {
      return (
        <div
          className="chat-content-reply__msg chat-content-reply__msg--italic"
          onClick={onScrollToReply}
        >
          Attachment
          {isPinned && <img className="chat-content-pin" src={pinImage} />}
        </div>
      );
    }

    if (message.replied.location) {
      return (
        <div
          className="chat-content-reply__msg chat-content-reply__msg--italic"
          onClick={onScrollToReply}
        >
          Location
          {isPinned && <img className="chat-content-pin" src={pinImage} />}
        </div>
      );
    }

    return (
      <div
        className="chat-content-reply__msg chat-content-reply__msg--italic"
        onClick={onScrollToReply}
      >
        Image
        {isPinned && <img className="chat-content-pin" src={pinImage} />}
      </div>
    );
  };

  const _getReplyInfo = () => {
    if (isOwner && message.replied.createdBy === user._id) {
      return `You replied to yourself`;
    }

    if (isOwner && message.replied.createdBy !== user._id) {
      return `You replied to ${_getEndUsersName()}`;
    }

    if (message.replied.createdBy === user._id) {
      return `${_getEndUsersName()} replied to you`;
    }

    return `${_getEndUsersName()} replied to themself`;
  };

  return (
    <MessageContainer
      id={`chat-${message._id}`}
      alignment={isOwner ? "right" : ""}
      onMouseLeave={handleMouseLeftMessageContainer}
    >
      {message.replied && (
        <div className="chat-content-reply">
          <div className="chat-content-reply__info">
            <ReplySvg width={12} />
            {_getReplyInfo()}
          </div>
          {_getReplyContent()}
        </div>
      )}
      <div
        className={`chat-content-msg${
          isOwner ? " chat-content-msg--right" : ""
        }`}
      >
        {isOwner && user.company && (
          <div className="chat-content-msg__avatar">
            <img alt="chat-avatar" src={`${assetsUri}/${user.avatar}`} />
          </div>
        )}
        {!isOwner && (
          <div className="chat-content-msg__avatar">
            <img
              alt="chat-avatar"
              src={`${assetsUri}/${
                session.endUser.avatar ?? session.endUser.logo
              }`}
              onError={({ target }) => {
                target.src = session.endUser.avatar ?? session.endUser.logo;
              }}
            />
          </div>
        )}
        {message.msg && (
          <div className="chat-content-msg-text" key={`msg-${message._id}`}>
            {message.msg?.split("\n").map((text, i) => (
              <div
                key={`msg-${message._id}-${i}`}
                className="chat-content-msg-text__line"
              >
                {text}
              </div>
            ))}
            {isPinned && !message.replied && (
              <img className="chat-content-pin" src={pinImage} />
            )}
          </div>
        )}
        {_displayLocation()}
        {_displayImages()}
        {_displayFiles()}
        {message.createdOn && (
          <div className="chat-content-msg__date">
            {format(new Date(message.createdOn), "HH:mm")}
          </div>
        )}
        <div className="chat-content-msg-actions">
          <div
            className="chat-content-msg-actions-action"
            onClick={() => {
              setIsToggleOption(false);
              onReply && onReply();
            }}
          >
            <ReplySvg
              className="chat-content-msg-actions-action__icon"
              height={18}
            />
          </div>
          {!session.support && (isOwner || (!isOwner && message.msg)) && (
            <div className="chat-content-msg-actions-action">
              <OptionMenuSvg
                className="chat-content-msg-actions-action__icon"
                height={18}
                onClick={() => setIsToggleOption((curr) => !curr)}
              />
              {isToggleOption && (
                <div className="chat-content-msg-actions-menu">
                  {isOwner && (
                    <div
                      className="chat-content-msg-actions-menu__item"
                      onClick={handleDeleteClick}
                    >
                      Unsend
                    </div>
                  )}
                  {message.msg && !isPinned && (
                    <div
                      className="chat-content-msg-actions-menu__item"
                      onClick={() => {
                        setIsPinned(true);
                        onPin && onPin();
                      }}
                    >
                      Pin
                    </div>
                  )}
                  {isPinned && (
                    <div
                      className="chat-content-msg-actions-menu__item"
                      onClick={() => {
                        setIsPinned(false);
                        onUnpin && onUnpin();
                      }}
                    >
                      Unpin
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MessageContainer>
  );
};

UserMessage.propTypes = {};

export default UserMessage;
